// PrepPack — submit a long-running interview_prep job and tail its log.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import Progress from '../components/ui/Progress';
import { apiGet, apiPost } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import { useSSE } from '../lib/sse';
import type { Job } from '../lib/types';

export default function PrepPack() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [jobId, setJobId] = useState<string | null>(null);
  const [stage, setStage] = useState('Technical Screen');

  const dataQ = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });
  const app = dataQ.data?.applications?.find((a) => a.id === sessionId || String(a.num) === sessionId);

  const startMut = useMutation({
    mutationFn: () =>
      apiPost<{ job_id: string; status: string } | Job>('/api/jobs', {
        cmd: 'interview_prep',
        payload: { company: app?.company, role: app?.role, stage, application_id: sessionId },
      }),
    onSuccess: (job) => {
      const id = (job as { job_id?: string }).job_id || (job as Job).id;
      setJobId(id);
    },
  });

  const sse = useSSE(jobId ? `/api/jobs/${jobId}/stream` : null, { enabled: !!jobId });

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/interview')}>← Interviews</button>
      <PageHeader
        eyebrow={app ? `Prep pack · ${app.company}` : 'Prep pack'}
        title="Everything you need for the round."
        sub="We'll generate a prep pack using interview-backend.mjs (Ollama). The job survives page refresh — tail it here or from /interview."
      />

      {!app ? (
        <div className="empty"><span className="serif">Session not found.</span>Go back and pick one.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 24, alignItems: 'start' }}>
          <div className="card" style={{ padding: 22 }}>
            <div className="tab" style={{ marginBottom: 8 }}>Target</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{app.company}</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 18 }}>{app.role}</div>

            <div className="tab" style={{ marginBottom: 6 }}>Stage</div>
            <select value={stage} onChange={(e) => setStage(e.target.value)} style={inputStyle}>
              {['Phone screen', 'Technical Screen', 'System Design', 'Behavioral', 'Coding', 'Bar Raiser', 'Onsite'].map((s) => <option key={s}>{s}</option>)}
            </select>

            <button
              className="btn accent lg"
              style={{ width: '100%', marginTop: 18 }}
              disabled={startMut.isPending || (!!jobId && sse.status === 'streaming')}
              onClick={() => startMut.mutate()}
            >
              {startMut.isPending || sse.status === 'streaming' ? 'Generating…' : 'Generate prep pack'}
            </button>
            {startMut.isError && (
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>
                {(startMut.error as Error).message}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 22, minHeight: 400 }}>
            <div className="tab" style={{ marginBottom: 10 }}>Output</div>
            {!jobId && <div className="empty" style={{ padding: '32px 12px' }}><span className="serif">Ready when you are.</span>Click Generate to start the job.</div>}
            {jobId && (
              <>
                <Progress value={sse.status === 'done' ? 100 : Math.min(95, sse.events.length * 3)} />
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6, marginBottom: 14 }}>
                  Job {jobId.slice(0, 8)} · {sse.status} · {sse.events.length} events
                </div>
                <div
                  style={{
                    fontFamily: 'Fraunces, var(--cjk-serif), serif',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    color: 'var(--ink)',
                    lineHeight: 1.7,
                    maxHeight: 500,
                    overflowY: 'auto',
                  }}
                >
                  {sse.tokens || sse.events.map((e) => (e.type === 'log' ? (e.data as { line?: string }).line : '')).filter(Boolean).join('\n') || 'Waiting for output…'}
                </div>
                {sse.error && (
                  <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>{sse.error}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
